import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { useApp } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { Plus, User, Download, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { PersonaPreviewCard } from '../components/PersonaPreviewCard';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Personas = () => {
  const { selectedProject } = useApp();
  const navigate = useNavigate();
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportingId, setExportingId] = useState(null);
  const exportContainerRef = useRef(null);

  useEffect(() => {
    if (selectedProject) {
      fetchPersonas();
    }
  }, [selectedProject]);

  const fetchPersonas = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/personas/projects/${selectedProject.id}/personas`
      );
      setPersonas(response.data);
    } catch (error) {
      console.error('Error fetching personas:', error);
      toast.error('Erro ao carregar personas');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePersona = () => {
    navigate('/personas/new');
  };

  const handleEditPersona = (personaId) => {
    navigate(`/personas/${personaId}/edit`);
  };

  const handleExportPdf = async (e, personaId, personaName) => {
    e.stopPropagation(); // Prevent card click
    
    try {
      setExportingId(personaId);
      
      // Find the persona data
      const persona = personas.find(p => p.id === personaId);
      if (!persona) {
        throw new Error('Persona not found');
      }
      
      // Create off-screen container for clean capture
      // This ensures no UI interference (scroll, overflow, clipping)
      const offscreenContainer = document.createElement('div');
      offscreenContainer.id = 'persona-export-root';
      offscreenContainer.style.cssText = `
        position: fixed;
        left: -9999px;
        top: 0;
        width: 900px;
        min-height: 100px;
        background: white;
        overflow: visible;
        z-index: -9999;
        pointer-events: none;
      `;
      document.body.appendChild(offscreenContainer);
      
      // Render a clean PersonaPreviewCard into the off-screen container
      const root = ReactDOM.createRoot(offscreenContainer);
      
      // Create a promise to wait for render completion
      await new Promise((resolve) => {
        root.render(
          <div 
            ref={(el) => {
              if (el) {
                exportContainerRef.current = el;
                // Wait for images to load and layout to settle
                setTimeout(resolve, 500);
              }
            }}
            style={{ 
              width: '900px', 
              background: 'white',
              overflow: 'visible'
            }}
          >
            <PersonaPreviewCard 
              persona={persona} 
              className="shadow-none border-0"
            />
          </div>
        );
      });
      
      // Get the rendered element
      const cardElement = exportContainerRef.current;
      if (!cardElement) {
        throw new Error('Export container not rendered');
      }
      
      // Wait a bit more for any async content (images, fonts)
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Get the FULL dimensions of the rendered content
      const fullWidth = cardElement.scrollWidth;
      const fullHeight = cardElement.scrollHeight;
      
      // Capture with html2canvas using full dimensions
      const canvas = await html2canvas(cardElement, {
        scale: 2, // High resolution for print quality
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        imageTimeout: 15000,
        width: fullWidth,
        height: fullHeight,
        windowWidth: fullWidth,
        windowHeight: fullHeight,
        scrollX: 0,
        scrollY: 0,
        x: 0,
        y: 0,
      });
      
      // Cleanup: unmount and remove off-screen container
      root.unmount();
      document.body.removeChild(offscreenContainer);
      
      // Get actual captured image dimensions
      const imgWidthPx = canvas.width;
      const imgHeightPx = canvas.height;
      
      // Convert to high-quality PNG
      const imageData = canvas.toDataURL('image/png', 1.0);
      
      // Send to backend with dimensions for proper scaling
      const response = await axios.post(
        `${API_URL}/api/personas/projects/${selectedProject.id}/personas/${personaId}/export-pdf-from-image`,
        { 
          imageData,
          personaName: personaName || 'persona',
          projectName: selectedProject.name || 'projeto',
          imgWidthPx,
          imgHeightPx
        },
        { responseType: 'blob' }
      );
      
      // Extract filename from response headers
      const contentDisposition = response.headers['content-disposition'];
      let filename = `persona-${(personaName || 'persona').toLowerCase().replace(/\s+/g, '-')}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      // Trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF exportado com sucesso!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Erro ao exportar PDF');
    } finally {
      setExportingId(null);
    }
  };

  if (!selectedProject) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Selecione um projeto para visualizar as personas.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin h-8 w-8 border-b-2 border-primary rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Personas</h1>
          <p className="text-sm text-muted-foreground">
            Perfis de usuários baseados em dados e pesquisas
          </p>
        </div>
        <Button onClick={handleCreatePersona}>
          <Plus className="mr-2 h-4 w-4" />
          Nova persona
        </Button>
      </div>

      {/* Personas List */}
      {personas.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <User className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Nenhuma persona criada</h3>
            <p className="text-sm text-muted-foreground text-center mb-4 max-w-md">
              Crie personas para representar os perfis de usuários do seu projeto.
              Você pode preencher manualmente ou usar dados do GA4 como base.
            </p>
            <Button onClick={handleCreatePersona}>
              <Plus className="mr-2 h-4 w-4" />
              Criar primeira persona
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {personas.map((persona) => (
            <div key={persona.id} className="relative group">
              <PersonaPreviewCard
                persona={persona}
                onClick={() => handleEditPersona(persona.id)}
                exportId={`persona-card-${persona.id}`}
              />
              {/* Export PDF button overlay */}
              <Button
                variant="secondary"
                size="sm"
                className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity shadow-md z-10"
                onClick={(e) => handleExportPdf(e, persona.id, persona.name)}
                disabled={exportingId === persona.id}
                title="Exportar PDF"
              >
                {exportingId === persona.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-1" />
                    PDF
                  </>
                )}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
